import React, {useCallback, useContext, useState, useRef, useEffect, useMemo} from 'react';
import {Button, Card, Text, Icon, SplitButton, InputStepper} from '@albert-io/atomic';
import {List} from 'immutable';
import {Manager, Target, Popper} from 'react-popper';
import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';

import {ContentDiscoveryContext} from '../ContentDiscoveryProvider.react';

import './add-questions-dropdown.scss';

interface AddQuestionsDropdownProps {
  maxQuestions: number;
}

const mergeQuestionLists = (selected: List<any>, toAdd: List<any>) => {
  const selectedIds = selected.toArray().map((s) => s.getId());
  const newSelected = toAdd.toArray().filter((s) => !selectedIds.includes(s.getId()));
  return List([...selected.toArray(), ...newSelected]);
};

const AddQuestionsDropdown = ({maxQuestions}: AddQuestionsDropdownProps) => {
  const {
    setSelectedQuestionSets,
    isQuestionSetSelected,
    questionsQuery,
    addXQuestionsVal: questionNum,
    setAddXQuestionsVal: setQuestionNum
  } = useContext(ContentDiscoveryContext);

  const [resultsManager, setResultsManager] = useState<PaginatedResultsManager>(
    {} as PaginatedResultsManager
  );
  const [previouslyPressed, setPreviouslyPressed] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showPopup, toggleShowPopup] = useState(false);
  const [hasEnoughQuestions, setHasEnoughQuestions] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(
    dropdownRef,
    useCallback(() => !!dropdownRef && toggleShowPopup(false), [dropdownRef, toggleShowPopup])
  );

  useEffect(() => {
    if (questionsQuery) {
      const newResultsManager = new PaginatedResultsManager({
        initialQuery: questionsQuery
      });
      setResultsManager(newResultsManager);
      // reset values for new query
      setHasEnoughQuestions(true);
      setPreviouslyPressed(false);
    } else {
      setHasEnoughQuestions(false);
    }
  }, [questionsQuery]);

  const addQuestions = useCallback(async () => {
    setIsAdding(true);
    let addedQuestionsNum = 0;
    let query = resultsManager.getResults();
    let hasQuestionsToAdd = true;
    while (addedQuestionsNum < questionNum && hasQuestionsToAdd) {
      try {
        /* eslint-disable no-await-in-loop */
        const questionsToAdd = await query;

        if (!questionsToAdd) {
          hasQuestionsToAdd = false;
          setHasEnoughQuestions(false);
        } else {
          let addedQuestions = questionsToAdd.filter((result) => !isQuestionSetSelected(result));

          const questionsLeftToAdd = questionNum - addedQuestionsNum;

          if (addedQuestions.size > questionsLeftToAdd) {
            addedQuestions = addedQuestions.slice(0, questionsLeftToAdd);
          }

          setSelectedQuestionSets((prev) => mergeQuestionLists(addedQuestions, prev));

          addedQuestionsNum += addedQuestions.size;

          if (addedQuestionsNum < questionNum) {
            query = resultsManager.fetchNextPage();
          }
        }
      } catch (err) {
        hasQuestionsToAdd = false;
      }
    }

    setTimeout(() => {
      // artificial loading time for user feedback
      setPreviouslyPressed(true);
      setIsAdding(false);
    }, 500);
  }, [resultsManager, isQuestionSetSelected, setSelectedQuestionSets, questionNum, setIsAdding]);

  const minNum = 1;

  const minError = questionNum < minNum;
  const hasError = minError;
  const errorMessage = `Choose a number between ${minNum} and 250`;

  const maxLimit = maxQuestions > 250 ? 250 : maxQuestions;

  const buttonText = useMemo(() => {
    if (isAdding) {
      return <>Adding...&nbsp;</>;
    }
    if (previouslyPressed) {
      return <>Add {questionNum}&nbsp;More</>;
    }
    return <>Add {questionNum}&nbsp;For Me</>;
  }, [isAdding, questionNum, previouslyPressed]);

  return (
    <div ref={dropdownRef}>
      <Manager>
        <SplitButton>
          <Button
            disabled={isAdding || hasError || !hasEnoughQuestions}
            variant='faded'
            size='s'
            onClick={() => {
              addQuestions();
            }}
            className='u-white-space_nowrap'
          >
            <span>
              {(isAdding || resultsManager?.requestInFlight) && (
                <Icon icon='spinner' spin className='u-mar-r_1' />
              )}
              {!hasEnoughQuestions && !isAdding && <Icon icon='warning' className='u-mar-r_1' />}
            </span>
            {buttonText}
          </Button>
          <Target>
            <Button
              variant='faded'
              onClick={() => {
                toggleShowPopup((prev) => !prev);
              }}
              ref={buttonRef}
            >
              <Icon icon='caret-down' />
            </Button>
          </Target>
        </SplitButton>
        {showPopup && (
          <Popper
            className='add-x-questions__popover'
            placement='bottom-start'
            target={buttonRef.current}
          >
            <Card
              paddingLevel={1}
              className='u-display_flex u-flex-direction_column u-align-items_center u-pad-t_2'
            >
              <Text bold color='black' as='p' className='u-text-align_center'>
                Customize Increment
              </Text>
              <InputStepper
                value={questionNum}
                min={minNum}
                max={maxLimit}
                errorMessage={errorMessage}
                hasError={hasError}
                onChange={(v) => setQuestionNum(Number(v))}
                ariaLabel='input-stepper'
              />

              {!hasEnoughQuestions && !hasError && (
                <>
                  <Icon icon={['far', 'warning']} className='u-mar-b_1' />
                  <Text size='s' className='u-text-align_center u-pad-lr_2'>
                    Increment is higher than the amount of questions in your results
                  </Text>
                </>
              )}
              <div className='add-x-questions__popover-help-container'>
                <Text emphasis='lowest' size='s'>
                  Tip: Press &quot;Add&quot; multiple times to continually add more questions.
                </Text>
              </div>
            </Card>
          </Popper>
        )}
      </Manager>
    </div>
  );
};

export default AddQuestionsDropdown;
